import {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import {ImageGetListSchema} from '../../../api/DBSchema/Image';
import {ObjectTypes, ApiErrorCodes, SortDirections} from '../../../api/consts';
import {useTranslation} from 'react-i18next';
import {push as navigateTo} from 'connected-react-router';
import {useGenericResultsDataApi} from '../../../api/hooks';
import {useSMCSnackbar, useColumnsWidthsPersistedState, useSort, useColumnsStatePersistedState} from '../../../utils/hooks';
import {SMCPaths} from '../../../conf/Routes';
import store from '../../../store';
import EditObjectCell from '../../../components/core/SMCTable/CommonCells/EditObjectCell';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import {buildStringSortCaseInsensitive} from '../../../utils/sortUtils';

export function useImageResults() {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();
    const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useSMCSnackbar();
    const { sortBasis, sortDirection, setSort } = useSort('name', SortDirections.ASCENDING);

    const columns = useMemo(() => ([
        { id: 'name',           accessor: ImageGetListSchema.name,           header: t('SMP.NAME'),                      width: 160, defaultShow: true, sort: buildStringSortCaseInsensitive(ImageGetListSchema.name),           Cell: EditObjectCell },
        { id: 'usageInReports', accessor: ImageGetListSchema.usageInReports, header: t('SMP.ADVANCED.USAGE_IN_REPORTS'), width: 300, defaultShow: true, sort: buildStringSortCaseInsensitive(ImageGetListSchema.usageInReports),                      },
        { id: 'description',    accessor: ImageGetListSchema.description,    header: t('SMC_GUI_COMMENT'),               width: 150, defaultShow: true, sort: buildStringSortCaseInsensitive(ImageGetListSchema.description),                         }
    ]), [t]);

    const rowIdSelector = obj => obj[ImageGetListSchema.name];

    const defaultColumnsState = useMemo(() => (
        columns.map(column => ({
            id: column.id,
            show: Boolean(column.defaultShow)
        }))
    ), [columns]);
    const [columnsState, setColumnsState] = useColumnsStatePersistedState(ObjectTypes.IMAGE, defaultColumnsState);
    const setDefaultColumnsState = useCallback(() => setColumnsState(defaultColumnsState), [defaultColumnsState, setColumnsState]);

    const [columnsWidths, setColumnsWidths] = useColumnsWidthsPersistedState(ObjectTypes.IMAGE, null);

    const [isPageLoaded, setIsPageLoaded] = useState(false);
    const isFirstRender = useRef(true);

    const {
        tableContent,
        isFetching,
        fetchNextPage,
        isLastPage,
        reloadTable,
        reloadTableNoClearSelected,
        selectedObjects,
        setSelectedObjects,
        deleteSelectedObjects,
        filterObject,
        setFilterObject,
        freeTextSearch
    } = useGenericResultsDataApi(ObjectTypes.IMAGE);

    const newObjectCallback = useCallback((objectId, errorCode) => {
        if (errorCode) {
            alert(t(errorCode));
        } else if (objectId) {
            store.dispatch(navigateTo({search: 'id=' + objectId}));
        }
    }, [t]);

    const reloadButtonOnClick = useCallback(() => {
        reloadTable()
            .catch(errorCode => {
                enqueueErrorSnackbar(t(errorCode));
            });
    }, [reloadTable, enqueueErrorSnackbar, t]);

    const deleteButtonOnClick = useCallback(() => {
        if (!Array.isArray(selectedObjects) || selectedObjects.length === 0) {
            alert(t('SMC_GEN_NO_SELECT'));
            return;
        }
        if (window.confirm(t('RESULTS_OBJECTS_DELETE_QUES'))) {
            setIsPageLoaded(false);
            deleteSelectedObjects()
                    .then(({successMessage, errorMessage}) => {
                        successMessage && enqueueSuccessSnackbar(successMessage);
                        errorMessage && enqueueErrorSnackbar(errorMessage);
                        reloadTable();
                    })
                    .catch(e => {
                        const errorMessage = getErrorMessage(e);
                        enqueueErrorSnackbar(errorMessage);
                    })
                    .finally(() => {
                        setIsPageLoaded(true);
                    });
        }
    }, [selectedObjects, deleteSelectedObjects, reloadTable, enqueueSuccessSnackbar, enqueueErrorSnackbar, getErrorMessage, t]);

    // Load table on first time loading
    useEffect(() => {
        reloadTable()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
    }, [reloadTable,  enqueueErrorSnackbar, t]);

    // Reload table without clearing selected on filter changed
    useEffect(() => {
        // Don't run on first render, only when filter is changed
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        reloadTableNoClearSelected()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
    }, [reloadTableNoClearSelected, filterObject, enqueueErrorSnackbar, t]);    

    return ({
        tableContent,
        isFetching,
        filterObject,
        setFilterObject,
        freeTextSearch,
        isPageLoaded,
        fetchNextPage,
        isLastPage,
        reloadTable,
        columns,
        sortBasis,
        sortDirection,
        setSort,
        columnsState,
        setColumnsState,
        setDefaultColumnsState,
        columnsWidths,
        setColumnsWidths,
        rowIdSelector,
        selectedObjects,
        setSelectedObjects,
        newObjectCallback,
        reloadButtonOnClick,
        deleteButtonOnClick
    });
}